import { z } from 'zod';
import { parseWebApiResponse } from './common';
const nullableStringSchema = z.string().nullable().optional();
const availabilityPortfolioEntrySchema = z.union([
    z.coerce.number().int(),
    z
        .object({
        id: z.coerce.number().int(),
        name: z.string().nullable().optional(),
    })
        .passthrough(),
    z
        .object({
        portfolioId: z.coerce.number().int(),
        portfolioName: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
    })
        .passthrough(),
]);
export const insightsDataAvailabilityResponseSchema = z
    .object({
    hasData: z.coerce.boolean().optional(),
    portfoliosWithData: z.array(availabilityPortfolioEntrySchema).optional().default([]),
})
    .passthrough()
    .transform((value) => ({
    ...value,
    hasData: value.hasData ?? value.portfoliosWithData.length > 0,
}));
export const communityPortfolioIdsResponseSchema = z.array(z
    .object({
    id: z.coerce.number().int(),
    name: z.string().nullable().optional(),
    parentPortfolioId: z.coerce.number().int().nullable(),
    parentPortfolioName: z.string().nullable(),
    buildingStates: z.array(z.string()),
    engagementLaunchDate: nullableStringSchema,
    createdAt: z.string(),
    assignedManagers: z
        .array(z.object({
        id: z.coerce.number().int(),
        name: z.string(),
    }))
        .default([]),
})
    .passthrough());
const insightsFreshnessSchema = z
    .object({
    dataAsOf: nullableStringSchema,
    computedAt: nullableStringSchema,
    source: z.string(),
})
    .passthrough();
const residentReachCohortSchema = z
    .object({
    key: z.enum(['current', 'notice', 'future', 'past', 'unmatched']),
    label: z.string(),
    sourceStatuses: z.array(z.string()).default([]),
    residentsContacted: z.coerce.number(),
    residentsReplied: z.coerce.number(),
    unitsContacted: z.coerce.number().nullable().optional(),
    replyRate: z.coerce.number().nullable().optional(),
    countsTowardOccupiedUnits: z.coerce.boolean(),
    countsTowardPeopleReached: z.coerce.boolean(),
})
    .passthrough();
const kpiPeriodMetricsSchema = z
    .object({
    totalTexts: z.coerce.number().nullable().optional(),
    textsSent: z.coerce.number().nullable().optional(),
    textsReceived: z.coerce.number().nullable().optional(),
    handledMessages: z.coerce.number().nullable().optional(),
    timeSavedHours: z.coerce.number().nullable().optional(),
    engagementRate: z.coerce.number().nullable().optional(),
    unitsEngaged: z.coerce.number().nullable().optional(),
    unitsContacted: z.coerce.number().nullable().optional(),
    currentReachableEngagementRate: z.coerce.number().nullable().optional(),
    currentReachableResidentsEngaged: z.coerce.number().nullable().optional(),
    currentReachableResidentsContacted: z.coerce.number().nullable().optional(),
    currentReachableUnitsContacted: z.coerce.number().nullable().optional(),
    residentReachCohorts: z.array(residentReachCohortSchema).optional().default([]),
    peopleReached: z.coerce.number().nullable().optional(),
    peopleReplied: z.coerce.number().nullable().optional(),
    peopleReplyRate: z.coerce.number().nullable().optional(),
    occupiedUnitsReached: z.coerce.number().nullable().optional(),
    includesMoveOutOutreach: z.coerce.boolean().optional(),
    toursScheduled: z.coerce.number().nullable().optional(),
    averageRating: z.coerce.number().nullable().optional(),
    reviewCount: z.coerce.number().nullable().optional(),
    paydownPct: z.coerce.number().nullable().optional(),
    paydownStartingWeight: z.coerce.number().nullable().optional(),
    paydownEndingWeight: z.coerce.number().nullable().optional(),
    paydownCollectionsWeight: z.coerce.number().nullable().optional(),
    total: z.coerce.number().nullable().optional(),
    needsAction: z.coerce.number().nullable().optional(),
    needsActionWithResponse: z.coerce.number().nullable().optional(),
    needsActionWithoutResponse: z.coerce.number().nullable().optional(),
    needsActionLateResponseCount: z.coerce.number().nullable().optional(),
    responseRate: z.coerce.number().nullable().optional(),
    handledByAI: z.coerce.number().nullable().optional(),
})
    .passthrough();
const googleProfileMetricsSchema = z
    .object({
    totalReviews: z.coerce.number().nullable().optional(),
    averageRating: z.coerce.number().nullable().optional(),
    latestFetchAt: nullableStringSchema,
    matchedPortfolioCount: z.coerce.number().nullable().optional(),
    matchedGooglePlaceCount: z.coerce.number().nullable().optional(),
    requestedPortfolioCount: z.coerce.number().nullable().optional(),
    source: z.string().nullable().optional(),
})
    .passthrough();
const kpiModuleSummarySchema = z
    .object({
    available: z.coerce.boolean().optional(),
    periods: z.record(z.string(), kpiPeriodMetricsSchema).optional(),
    inFlight: z.coerce.number().nullable().optional(),
    currentGoogleProfile: googleProfileMetricsSchema.nullable().optional(),
})
    .passthrough();
export const insightsKpiPortfolioBreakdownSchema = z
    .object({
    portfolioId: z.coerce.number().int(),
    summary: z
        .object({
        messaging: kpiModuleSummarySchema.optional(),
        leasing: kpiModuleSummarySchema.optional(),
        reviews: kpiModuleSummarySchema.optional(),
        delinquency: kpiModuleSummarySchema.optional(),
        escalations: kpiModuleSummarySchema.optional(),
        renewals: kpiModuleSummarySchema.optional(),
    })
        .passthrough(),
})
    .passthrough();
export const insightsKpiPortfolioBreakdownsResponseSchema = z.array(insightsKpiPortfolioBreakdownSchema);
const insightsKpiSummarySchema = z
    .object({
    portfolioBreakdowns: insightsKpiPortfolioBreakdownsResponseSchema.optional(),
    fetchedAt: nullableStringSchema,
})
    .passthrough();
export const insightsKpiJobResponseSchema = z
    .object({
    job: z
        .object({
        jobId: z.string().uuid(),
        status: z.enum(['queued', 'running', 'completed', 'failed']),
        phase: z.string(),
        phaseLabel: z.string(),
        progressPct: z.coerce.number(),
        requestedAt: z.string(),
        startedAt: nullableStringSchema,
        completedAt: nullableStringSchema,
        errorMessage: nullableStringSchema,
        scope: z
            .object({
            portfolioIds: z.array(z.coerce.number().int()),
        })
            .passthrough(),
        summary: insightsKpiSummarySchema.nullable().optional(),
        freshness: insightsFreshnessSchema.nullable().optional(),
    })
        .passthrough(),
})
    .strip();
export function getInsightsAvailabilityPortfolioIds(portfoliosWithData = []) {
    const portfolioIds = [];
    for (const portfolio of portfoliosWithData) {
        let portfolioId;
        if (typeof portfolio === 'number') {
            portfolioId = portfolio;
        }
        else if (portfolio && typeof portfolio === 'object') {
            if ('id' in portfolio) {
                portfolioId = portfolio.id;
            }
            else if ('portfolioId' in portfolio) {
                portfolioId = portfolio.portfolioId;
            }
        }
        const normalizedPortfolioId = Number(portfolioId);
        if (Number.isFinite(normalizedPortfolioId)) {
            portfolioIds.push(normalizedPortfolioId);
        }
    }
    return portfolioIds;
}
export function normalizeInsightsPortfolioIds(portfolios = []) {
    const portfolioIds = portfolios
        .map((portfolio) => {
        if (portfolio === null || portfolio === undefined)
            return undefined;
        if (portfolio && typeof portfolio === 'object') {
            if ('id' in portfolio)
                return portfolio.id;
            if ('portfolioId' in portfolio)
                return portfolio.portfolioId;
            return undefined;
        }
        return portfolio;
    })
        .map((portfolioId) => Number(portfolioId))
        .filter((portfolioId) => Number.isInteger(portfolioId) && portfolioId > 0);
    return [...new Set(portfolioIds)].sort((left, right) => left - right);
}
export function parseInsightsDataAvailabilityResponse(data, endpoint = 'GET /api/*/data-availability/account') {
    return parseWebApiResponse(insightsDataAvailabilityResponseSchema, data, endpoint);
}
export function parseCommunityPortfolioIdsResponse(data) {
    return parseWebApiResponse(communityPortfolioIdsResponseSchema, data, 'GET /api/portfolios/all/community-ids');
}
export function parseInsightsKpiJobResponse(data, endpoint = 'GET /api/insights-kpi/jobs/:jobId') {
    return parseWebApiResponse(insightsKpiJobResponseSchema, data, endpoint);
}
export function parseInsightsKpiPortfolioBreakdownsResponse(data) {
    return parseWebApiResponse(insightsKpiPortfolioBreakdownsResponseSchema, data, 'GET /api/insights-kpi/portfolio-breakdowns');
}
